import { forexBasicsCategories } from '@/mocks/forexBasics';
import { allArticles } from '@/utils/content';
import { categories } from '@/mocks/categories';

const STORAGE_KEY = 'forex:read-articles';
const LEGACY_STORAGE_KEY = 'forex-basics:read-articles';

export type BasicsArticleEntry = {
  slug: string;
  label: string;
  to: string;
  categoryId: string;
  categoryTitle: string;
};

/** The 30 basics articles, kept in the exact learning order shown on the page. */
export const basicsArticleOrder: BasicsArticleEntry[] = forexBasicsCategories.flatMap((category) =>
  category.topics.map((topic) => ({
    slug: topic.to.replace(/^\//, ''),
    label: topic.label,
    to: topic.to,
    categoryId: category.id,
    categoryTitle: category.title,
  })),
);

export const basicsTotalCount = basicsArticleOrder.length;

export type LearningTrack = {
  /** Category slug, e.g. "forex-basics". */
  slug: string;
  /** Thai display name of the track. */
  name: string;
  /** Font Awesome / Remix icon class. */
  icon: string;
  /** Every article slug that belongs to this track, in page order. */
  slugs: string[];
};

/** Every learning section that has articles becomes a track that can be completed. */
export const learningTracks: LearningTrack[] = categories
  .map((category) => ({
    slug: category.slug,
    name: category.name,
    icon: category.icon,
    slugs: allArticles
      .filter((article) => article.categorySlug === category.slug)
      .map((article) => article.slug),
  }))
  .filter((track) => track.slugs.length > 0);

const slugToTrack = new Map<string, string>();
learningTracks.forEach((track) => {
  track.slugs.forEach((slug) => slugToTrack.set(slug, track.slug));
});

const basicsSlugSet = new Set(basicsArticleOrder.map((entry) => entry.slug));
const trackSlugSet = new Set(slugToTrack.keys());

let listeners: Array<() => void> = [];
let cache: string[] = [];
let initialized = false;

function readFromStorage(): string[] {
  if (typeof window === 'undefined') return [];
  let raw = window.localStorage.getItem(STORAGE_KEY);
  if (!raw) {
    // Migrate progress saved by the earlier basics-only version.
    raw = window.localStorage.getItem(LEGACY_STORAGE_KEY);
  }
  if (!raw) return [];
  try {
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    const unique = Array.from(
      new Set(parsed.filter((item): item is string => typeof item === 'string')),
    );
    // Keep only slugs that still belong to a known track, in a stable order.
    return Array.from(trackSlugSet).filter((slug) => unique.includes(slug));
  } catch {
    return [];
  }
}

function emit() {
  listeners.forEach((listener) => listener());
}

function persist(next: string[]) {
  cache = next;
  initialized = true;
  if (typeof window !== 'undefined') {
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
    } catch {
      /* storage unavailable - keep in-memory progress only */
    }
  }
  emit();
}

export function subscribe(listener: () => void) {
  listeners.push(listener);
  const onStorage = (event: StorageEvent) => {
    if (event.key === STORAGE_KEY || event.key === LEGACY_STORAGE_KEY) {
      cache = readFromStorage();
      initialized = true;
      emit();
    }
  };
  if (typeof window !== 'undefined') {
    window.addEventListener('storage', onStorage);
  }
  return () => {
    listeners = listeners.filter((item) => item !== listener);
    if (typeof window !== 'undefined') {
      window.removeEventListener('storage', onStorage);
    }
  };
}

export function getSnapshot(): string[] {
  if (!initialized) {
    cache = readFromStorage();
    initialized = true;
  }
  return cache;
}

export function getServerSnapshot(): string[] {
  return [];
}

export function isBasicsArticle(slug: string) {
  return basicsSlugSet.has(slug);
}

/** True for any article that belongs to a completable learning track. */
export function isTrackArticle(slug: string) {
  return trackSlugSet.has(slug);
}

export function getTrackSlugForArticle(slug: string) {
  return slugToTrack.get(slug) ?? null;
}

export function markArticleRead(slug: string) {
  if (!isTrackArticle(slug)) return;
  const current = getSnapshot();
  if (current.includes(slug)) return;
  persist([...current, slug]);
}

export function resetProgress() {
  persist([]);
}

/** Clears the read marks of a single learning track only. */
export function resetTrack(trackSlug: string) {
  const current = getSnapshot();
  persist(current.filter((slug) => slugToTrack.get(slug) !== trackSlug));
}

export function getBasicsEntry(slug: string) {
  return basicsArticleOrder.find((entry) => entry.slug === slug) ?? null;
}